import React from "react";
import { useInstagramForm } from "../../../../../hooks/useInstagramForm";
import { PlatformFormProps } from "./types";
import instagramIcon from "../../../../../assets/images/social/instagram.svg";

const IG_MAX_CHARS = 2200;
const IG_MAX_HASHTAGS = 30;

const charCountColor = (len: number, max: number) => {
  if (len > max) return "#dc2626";
  if (len >= max * 0.9) return "#d97706";
  return "#6b7280";
};

const InstagramForm: React.FC<PlatformFormProps> = ({
  postBody,
  setPostBody,
  mediaPreviewUrls,
  mediaMetadata,
  removeMedia,
  fileInputRef,
  draggedIndex,
  handleImageDragStart,
  handleImageDragOver,
  handleImageDrop,
  handleImageDragEnd,
  handleFileChange,
  handleDragOver,
  handleDrop,
  isStory,
  setIsStory,
  useCommonMediaForAllPlatforms,
  setUseCommonMediaForAllPlatforms,
  showCommonMediaToggle,
}) => {
  const { validation, requirements: REQUIREMENTS } = useInstagramForm(
    mediaMetadata,
    isStory
  );
  const maxImages =
    typeof REQUIREMENTS.maxImages === "number" ? REQUIREMENTS.maxImages : 10;

  return (
    <>
      {/* Content Fine-Tuning */}
      <div className="form-section">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          <label className="section-label" style={{ marginBottom: 0 }}>
            Content for Instagram
          </label>
        </div>

        {/* Story Toggle */}
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "10px",
            padding: "14px 16px",
            marginTop: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <img
                src={instagramIcon}
                alt="Instagram"
                style={{ width: 18, height: 18, opacity: 0.85 }}
              />
              <span
                style={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#1f2937",
                }}
              >
                Instagram Story
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isStory}
              onClick={() => setIsStory?.(!isStory)}
              style={{
                width: "40px",
                height: "22px",
                borderRadius: "11px",
                border: "none",
                background: isStory ? "#e1306c" : "#d1d5db",
                position: "relative",
                cursor: "pointer",
                transition: "background 0.2s",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  background: "#fff",
                  position: "absolute",
                  top: "3px",
                  left: isStory ? "21px" : "3px",
                  transition: "left 0.2s",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                }}
              />
            </button>
          </div>
          <p
            style={{
              margin: "6px 0 0",
              fontSize: "12px",
              color: "#6b7280",
              lineHeight: "1.4",
            }}
          >
            Post this content as an Instagram Story.
          </p>
        </div>

        {!isStory && (
          <div className="input-group">
            <label
              className="section-label"
              style={{ fontWeight: 500, color: "#6b7280" }}
            >
              Post Body Content
              <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
            </label>
            <textarea
              className="step3-textarea"
              value={postBody}
              onChange={(e) => setPostBody(e.target.value)}
              placeholder="Write your post content here..."
              required
            />
            {(() => {
              const hashtagCount = (postBody.match(/#\w+/g) || []).length;
              return (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "12px",
                    fontWeight: 500,
                    marginTop: "4px",
                  }}
                >
                  <span
                    style={{
                      color: charCountColor(hashtagCount, IG_MAX_HASHTAGS),
                    }}
                  >
                    {hashtagCount} / {IG_MAX_HASHTAGS} hashtags
                  </span>
                  <span
                    style={{
                      color: charCountColor(postBody.length, IG_MAX_CHARS),
                    }}
                  >
                    {postBody.length} / {IG_MAX_CHARS.toLocaleString()}
                  </span>
                </div>
              );
            })()}
            <div className="requirement-note">
              Media is required for Instagram posts
            </div>
          </div>
        )}

        {showCommonMediaToggle && (
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "14px 16px",
              marginTop: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#1f2937",
                  }}
                >
                  Common media for all platforms
                </span>
                <p
                  style={{
                    margin: "6px 0 0",
                    fontSize: "12px",
                    color: "#6b7280",
                    lineHeight: "1.4",
                  }}
                >
                  {useCommonMediaForAllPlatforms
                    ? "Use the same uploaded media across all selected platforms."
                    : "Send media in each platform-specific configuration."}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!useCommonMediaForAllPlatforms}
                onClick={() =>
                  setUseCommonMediaForAllPlatforms?.(
                    !useCommonMediaForAllPlatforms
                  )
                }
                style={{
                  width: "40px",
                  height: "22px",
                  borderRadius: "11px",
                  border: "none",
                  background: useCommonMediaForAllPlatforms
                    ? "#4f46e5"
                    : "#d1d5db",
                  position: "relative",
                  cursor: "pointer",
                  transition: "background 0.2s",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    background: "#fff",
                    position: "absolute",
                    top: "3px",
                    left: useCommonMediaForAllPlatforms ? "21px" : "3px",
                    transition: "left 0.2s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                  }}
                />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Media Assets */}
      <div className="form-section">
        <label className="section-label">
          Media Assets
          <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
        </label>

        {/* Platform Requirements Info */}
        <div className="platform-requirements">
          <div
            className="req-row"
            style={{
              background:
                validation.imageCountValid === true
                  ? "#f0fdf4"
                  : validation.imageCountValid === false
                  ? "#fef2f2"
                  : "transparent",
            }}
          >
            <span className="req-label">
              {validation.imageCountValid === true && (
                <span style={{ color: "#16a34a", marginRight: "6px" }}>✓</span>
              )}
              {validation.imageCountValid === false && (
                <span style={{ color: "#dc2626", marginRight: "6px" }}>✗</span>
              )}
              Images:
            </span>
            <span className="req-value">
              Up to {REQUIREMENTS.maxImages} • {REQUIREMENTS.imageFormats}
            </span>
          </div>
          <div
            className="req-row"
            style={{
              background:
                validation.imageSizeValid === true
                  ? "#f0fdf4"
                  : validation.imageSizeValid === false
                  ? "#fef2f2"
                  : "transparent",
            }}
          >
            <span className="req-label">
              {validation.imageSizeValid === true && (
                <span style={{ color: "#16a34a", marginRight: "6px" }}>✓</span>
              )}
              {validation.imageSizeValid === false && (
                <span style={{ color: "#dc2626", marginRight: "6px" }}>✗</span>
              )}
              Image Size:
            </span>
            <span className="req-value">Max {REQUIREMENTS.maxImageSize}</span>
          </div>
          <div
            className="req-row"
            style={{
              background:
                validation.dimensionsValid === true
                  ? "#f0fdf4"
                  : validation.dimensionsValid === false
                  ? "#fef2f2"
                  : "transparent",
            }}
          >
            <span className="req-label">
              {validation.dimensionsValid === true && (
                <span style={{ color: "#16a34a", marginRight: "6px" }}>✓</span>
              )}
              {validation.dimensionsValid === false && (
                <span style={{ color: "#dc2626", marginRight: "6px" }}>✗</span>
              )}
              Dimensions:
            </span>
            <span className="req-value">
              Min: {REQUIREMENTS.minDimensions} • Max:{" "}
              {REQUIREMENTS.maxDimensions}
            </span>
          </div>
          <div
            className="req-row"
            style={{
              background:
                validation.aspectRatiosValid === true
                  ? "#f0fdf4"
                  : validation.aspectRatiosValid === false
                  ? "#fef2f2"
                  : "transparent",
            }}
          >
            <span className="req-label">
              {validation.aspectRatiosValid === true && (
                <span style={{ color: "#16a34a", marginRight: "6px" }}>✓</span>
              )}
              {validation.aspectRatiosValid === false && (
                <span style={{ color: "#dc2626", marginRight: "6px" }}>✗</span>
              )}
              Aspect Ratios:
            </span>
            <span className="req-value">{REQUIREMENTS.aspectRatios}</span>
          </div>
          {REQUIREMENTS.maxVideos > 0 && (
            <>
              <div
                className="req-row"
                style={{
                  background:
                    validation.videoCountValid === true
                      ? "#f0fdf4"
                      : validation.videoCountValid === false
                      ? "#fef2f2"
                      : "transparent",
                }}
              >
                <span className="req-label">
                  {validation.videoCountValid === true && (
                    <span style={{ color: "#16a34a", marginRight: "6px" }}>
                      ✓
                    </span>
                  )}
                  {validation.videoCountValid === false && (
                    <span style={{ color: "#dc2626", marginRight: "6px" }}>
                      ✗
                    </span>
                  )}
                  Videos:
                </span>
                <span className="req-value">
                  Up to {REQUIREMENTS.maxVideos} • {REQUIREMENTS.videoFormats}
                </span>
              </div>
              <div
                className="req-row"
                style={{
                  background:
                    validation.videoDurationValid === true
                      ? "#f0fdf4"
                      : validation.videoDurationValid === false
                      ? "#fef2f2"
                      : "transparent",
                }}
              >
                <span className="req-label">
                  {validation.videoDurationValid === true && (
                    <span style={{ color: "#16a34a", marginRight: "6px" }}>
                      ✓
                    </span>
                  )}
                  {validation.videoDurationValid === false && (
                    <span style={{ color: "#dc2626", marginRight: "6px" }}>
                      ✗
                    </span>
                  )}
                  Video Size:
                </span>
                <span className="req-value">
                  Max {REQUIREMENTS.maxVideoSize} • Duration:{" "}
                  {REQUIREMENTS.videoDuration}
                </span>
              </div>
            </>
          )}
          <div className="req-row">
            <span className="req-label">Recommended:</span>
            <span className="req-value">{REQUIREMENTS.recommended}</span>
          </div>
          {REQUIREMENTS.note && (
            <div className="req-note">{REQUIREMENTS.note}</div>
          )}
        </div>

        {/* Warning for excess images */}
        {mediaPreviewUrls.length > maxImages && (
          <div
            style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "8px",
              padding: "10px 12px",
              marginBottom: "12px",
              fontSize: "13px",
              color: "#856404",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span style={{ fontSize: "16px" }}>⚠️</span>
            <span>
              Only {maxImages} {maxImages === 1 ? "image" : "images"} will be
              taken for Instagram. The first {maxImages} will be used.
            </span>
          </div>
        )}

        <div
          className="media-upload-area"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => {
            if (mediaPreviewUrls.length < maxImages) {
              fileInputRef.current?.click();
            }
          }}
          style={{
            cursor:
              mediaPreviewUrls.length >= maxImages ? "not-allowed" : "pointer",
            position: "relative",
            overflow: "hidden",
            padding: mediaPreviewUrls.length > 0 ? "10px" : "20px",
            display: "flex",
            flexDirection: mediaPreviewUrls.length > 0 ? "row" : "column",
            flexWrap: "wrap",
            gap: "10px",
            alignItems: "center",
            justifyContent:
              mediaPreviewUrls.length > 0 ? "flex-start" : "center",
          }}
        >
          {mediaPreviewUrls.length > 0 ? (
            <>
              {mediaPreviewUrls.map((url, index) => {
                const isDisabled = index >= maxImages;
                const media = mediaMetadata[index];
                const isVideo = media?.type === "video";

                return (
                  <div
                    key={url}
                    draggable
                    onDragStart={(e) => handleImageDragStart(e, index)}
                    onDragOver={handleImageDragOver}
                    onDrop={(e) => handleImageDrop(e, index)}
                    onDragEnd={handleImageDragEnd}
                    style={{
                      position: "relative",
                      width: "80px",
                      height: "80px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      flexShrink: 0,
                      cursor: "move",
                      opacity: isDisabled
                        ? 0.4
                        : draggedIndex === index
                        ? 0.5
                        : 1,
                      border: isDisabled
                        ? "2px solid #ef4444"
                        : draggedIndex === index
                        ? "2px solid #4f46e5"
                        : "2px solid transparent",
                      filter: isDisabled ? "grayscale(100%)" : "none",
                    }}
                  >
                    {isDisabled && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: "rgba(239, 68, 68, 0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 1,
                        }}
                      >
                        <span
                          style={{
                            background: "rgba(239, 68, 68, 0.9)",
                            color: "white",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "10px",
                            fontWeight: "600",
                          }}
                        >
                          DISABLED
                        </span>
                      </div>
                    )}

                    {isVideo ? (
                      <>
                        <video
                          src={url}
                          preload="metadata"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            pointerEvents: "none",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            background: "rgba(0, 0, 0, 0.6)",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                          }}
                        >
                          <div
                            style={{
                              width: 0,
                              height: 0,
                              borderLeft: "6px solid white",
                              borderTop: "4px solid transparent",
                              borderBottom: "4px solid transparent",
                              marginLeft: "2px",
                            }}
                          />
                        </div>
                        {media?.duration && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: 4,
                              right: 4,
                              background: "rgba(0, 0, 0, 0.7)",
                              color: "white",
                              padding: "2px 4px",
                              borderRadius: "3px",
                              fontSize: "9px",
                              fontWeight: "600",
                            }}
                          >
                            {Math.floor(media.duration / 60)}:
                            {String(Math.floor(media.duration % 60)).padStart(
                              2,
                              "0"
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <img
                        src={url}
                        alt={`Uploaded ${index}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          pointerEvents: "none",
                        }}
                      />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeMedia(index);
                      }}
                      style={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        background: "rgba(0,0,0,0.6)",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        zIndex: 2,
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              {mediaPreviewUrls.length < maxImages && (
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    border: "2px dashed #ccc",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ccc",
                    fontSize: "24px",
                    flexShrink: 0,
                  }}
                >
                  +
                </div>
              )}
            </>
          ) : (
            <>
              <div className="upload-icon">☁️</div>
              <span className="upload-text">Upload or drag media</span>
            </>
          )}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept={REQUIREMENTS.maxVideos > 0 ? "image/*,video/*" : "image/*"}
            multiple
            onChange={handleFileChange}
          />
        </div>
        {mediaPreviewUrls.length > 0 && (
          <div
            style={{
              fontSize: "12px",
              color:
                mediaPreviewUrls.length >= maxImages ? "#ef4444" : "#6b7280",
              marginTop: "4px",
              fontWeight: mediaPreviewUrls.length >= maxImages ? 500 : 400,
            }}
          >
            {mediaPreviewUrls.length} / {maxImages} file
            {mediaPreviewUrls.length !== 1 ? "s" : ""} selected
            {mediaPreviewUrls.length >= maxImages && " (Maximum reached)"}
          </div>
        )}
      </div>
    </>
  );
};

export default InstagramForm;
